import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

const AdditionalDetails = ({
  clubTimings, setClubTimings,
  lunchTime, setLunchTime,
  deliveryWindow, setDeliveryWindow,
  ageRange, setAgeRange,
  isDeliveryWindowConfirmed, setIsDeliveryWindowConfirmed,
  confirmationChecked, setConfirmationChecked,
  onSave, onPrev,
}) => {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    defaultValues: {
      clubTimingsFrom:    clubTimings.from,
      clubTimingsTo:      clubTimings.to,
      lunchTimeFrom:      lunchTime.from,
      lunchTimeTo:        lunchTime.to,
      deliveryWindowFrom: deliveryWindow.from,
      deliveryWindowTo:   deliveryWindow.to,
      ageRangeLower:      ageRange.lower,
      ageRangeUpper:      ageRange.upper,
    }
  });

  useEffect(() => {
    setValue('clubTimingsFrom',    clubTimings.from    || '');
    setValue('clubTimingsTo',      clubTimings.to      || '');
    setValue('lunchTimeFrom',      lunchTime.from      || '');
    setValue('lunchTimeTo',        lunchTime.to        || '');
    setValue('deliveryWindowFrom', deliveryWindow.from || '');
    setValue('deliveryWindowTo',   deliveryWindow.to   || '');
    setValue('ageRangeLower',      ageRange.lower      || '');
    setValue('ageRangeUpper',      ageRange.upper      || '');
  }, [clubTimings, lunchTime, deliveryWindow, ageRange, setValue]);

  return (
    <div className="tab-content">
      <h1>Schedule & Details</h1>
      <p className="tab-description">
        All fields below are required. This information helps us plan deliveries accurately.
      </p>
      <form onSubmit={handleSubmit(onSave)} className="business-form">

        {/* Club Timings */}
        <div className="form-group">
          <label>Club open hours <span className="req">*</span></label>
          <div className="time-range">
            <input
              type="time"
              value={clubTimings.from}
              {...register('clubTimingsFrom', {
                required: 'Club open time is required',
                onChange: (e) => setClubTimings({ ...clubTimings, from: e.target.value })
              })}
            />
            <span>to</span>
            <input
              type="time"
              value={clubTimings.to}
              {...register('clubTimingsTo', {
                required: 'Club close time is required',
                onChange: (e) => setClubTimings({ ...clubTimings, to: e.target.value })
              })}
            />
          </div>
          {(errors.clubTimingsFrom || errors.clubTimingsTo) && (
            <span className="error-text">
              {errors.clubTimingsFrom?.message || errors.clubTimingsTo?.message}
            </span>
          )}
        </div>

        {/* Lunch Time */}
        <div className="form-group">
          <label>Lunch time <span className="req">*</span></label>
          <div className="time-range">
            <input
              type="time"
              value={lunchTime.from}
              {...register('lunchTimeFrom', {
                required: 'Lunch start time is required',
                onChange: (e) => setLunchTime({ ...lunchTime, from: e.target.value })
              })}
            />
            <span>to</span>
            <input
              type="time"
              value={lunchTime.to}
              {...register('lunchTimeTo', {
                required: 'Lunch end time is required',
                onChange: (e) => setLunchTime({ ...lunchTime, to: e.target.value })
              })}
            />
          </div>
          {(errors.lunchTimeFrom || errors.lunchTimeTo) && (
            <span className="error-text">
              {errors.lunchTimeFrom?.message || errors.lunchTimeTo?.message}
            </span>
          )}
        </div>

        {/* Delivery Window */}
        <div className="form-group">
          <label>Preferred delivery window <span className="req">*</span></label>
          <div className="time-range">
            <input
              type="time"
              value={deliveryWindow.from}
              {...register('deliveryWindowFrom', {
                required: 'Delivery window start is required',
                onChange: (e) => setDeliveryWindow({ ...deliveryWindow, from: e.target.value })
              })}
            />
            <span>to</span>
            <input
              type="time"
              value={deliveryWindow.to}
              {...register('deliveryWindowTo', {
                required: 'Delivery window end is required',
                onChange: (e) => setDeliveryWindow({ ...deliveryWindow, to: e.target.value })
              })}
            />
          </div>
          {(errors.deliveryWindowFrom || errors.deliveryWindowTo) && (
            <span className="error-text">
              {errors.deliveryWindowFrom?.message || errors.deliveryWindowTo?.message}
            </span>
          )}
        </div>

        {/* Confirm delivery window */}
        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="deliveryConfirm"
            checked={isDeliveryWindowConfirmed}
            onChange={() => setIsDeliveryWindowConfirmed(!isDeliveryWindowConfirmed)}
          />
          <label htmlFor="deliveryConfirm">I confirm the delivery window above</label>
        </div>

        {/* Age Range */}
        <div className="form-group">
          <label>Age range of children <span className="req">*</span></label>
          <div className="time-range">
            <input
              type="number"
              placeholder="Min age"
              min="0"
              max="18"
              value={ageRange.lower}
              {...register('ageRangeLower', {
                required: 'Minimum age is required',
                min: { value: 0, message: 'Must be 0 or above' },
                onChange: (e) => setAgeRange({ ...ageRange, lower: e.target.value })
              })}
            />
            <span>to</span>
            <input
              type="number"
              placeholder="Max age"
              min="0"
              max="18"
              value={ageRange.upper}
              {...register('ageRangeUpper', {
                required: 'Maximum age is required',
                min: { value: 0, message: 'Must be 0 or above' },
                onChange: (e) => setAgeRange({ ...ageRange, upper: e.target.value })
              })}
            />
          </div>
          {(errors.ageRangeLower || errors.ageRangeUpper) && (
            <span className="error-text">
              {errors.ageRangeLower?.message || errors.ageRangeUpper?.message}
            </span>
          )}
        </div>

        {/* Final confirmation */}
        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="infoConfirm"
            checked={confirmationChecked}
            onChange={() => setConfirmationChecked(!confirmationChecked)}
          />
          <label htmlFor="infoConfirm">I confirm that all information provided is accurate</label>
        </div>

        <div className="form-navigation">
          <button type="button" onClick={onPrev}>← Previous</button>
          <button type="submit">Save all changes</button>
        </div>
      </form>
    </div>
  );
};

export default AdditionalDetails;
